import { memo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { format } from "date-fns"
import { IconAtom2Outline18 as Atom } from "nucleo-ui-outline-18"

import type { AgentOutcomeBucket } from "@productlane/lib/src/agent-outcome"
import type { Feedback } from "@productlane/zero"
import {
	classifyAgentOutcome,
	explainAgentOutcome,
	handedOffInSession,
} from "@productlane/lib/src/agent-outcome"
import { cn } from "@productlane/lib/src/cn"
import { Button, Tooltip } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { useTimeTick } from "@/lib/hooks/use-time-tick"

const LABELS: Record<AgentOutcomeBucket, string> = {
	resolved: "Resolved",
	escalated: "Escalated",
	pending: "Pending",
}

const TONES: Record<AgentOutcomeBucket, string> = {
	resolved: "!text-green-600 dark:!text-green-400",
	escalated: "!text-primary",
	pending: "!text-yellow-600 dark:!text-yellow-400",
}

export const AgentResolutionBadge = memo(
	({
		thread,
	}: {
		thread: Pick<Feedback, "id" | "state" | "lastInboundMessageAt">
	}) => {
		const [session] = useQuery(
			queries.threadAgentSession({ threadId: thread.id }),
		)
		const [activities] = useQuery(
			queries.threadAgentOutcomeActivities({ threadId: thread.id }),
		)
		// The pending countdown names a time, so it re-reads once a minute.
		useTimeTick()
		if (!session) return null
		const bucket = classifyAgentOutcome({
			isResolved: session.isResolved,
			escalatedAt: session.escalatedAt,
			threadState: thread.state,
			hasHandoffActivity: handedOffInSession(
				session.id,
				activities.filter((a) => a.eventType === "ai_handoff"),
			),
		})
		if (!bucket) return null
		const reason = explainAgentOutcome({
			bucket,
			sessionId: session.id,
			// Not replicated; a judged or assumed resolution still carries its own time.
			resolvedAt: null,
			escalatedAt: session.escalatedAt,
			updatedAt: session.updatedAt,
			lastInboundMessageAt: thread.lastInboundMessageAt,
			activities,
		})
		const label = reason
			? [reason.text, reason.at && format(reason.at, "MMM d, HH:mm")]
					.filter(Boolean)
					.join(" · ")
			: LABELS[bucket]
		return (
			<Tooltip label={label}>
				<Button
					variant="ghost"
					Icon={Atom}
					iconClassName="h-4 w-4 text-inherit"
					className={cn(
						"h-auto min-w-[145px] justify-start text-xs hover:bg-transparent dark:hover:bg-transparent",
						TONES[bucket],
					)}
				>
					{LABELS[bucket]}
				</Button>
			</Tooltip>
		)
	},
)
AgentResolutionBadge.displayName = "AgentResolutionBadge"
